import type { ValidationError } from '@/shared/api/intranet'
import { HTTP_STATUS } from '@/shared/lib'

import type { ApiErrorLike } from './apiErrorLike'

import { type CreateOrderError, CREATE_ORDER_ERROR } from '../model'
import {
  CREATE_ORDER_ADDRESS_FIELDS,
  CREATE_ORDER_API_DETAIL,
  CREATE_ORDER_API_DETAIL_FRAGMENT,
  CREATE_ORDER_VALIDATION_LOC,
} from './createOrderApiErrorDetail'

const VALIDATION_ERROR_MATCHERS: Array<{ code: CreateOrderError; match: (detail: ValidationError[]) => boolean }> = [
  {
    code: CREATE_ORDER_ERROR.invalidPhone,
    match: (detail) => detail.some((item) => item.loc.includes(CREATE_ORDER_VALIDATION_LOC.phone)),
  },
  {
    code: CREATE_ORDER_ERROR.invalidEmail,
    match: (detail) => detail.some((item) => item.loc.includes(CREATE_ORDER_VALIDATION_LOC.email)),
  },
  {
    code: CREATE_ORDER_ERROR.invalidPostalCode,
    match: (detail) => detail.some((item) => item.loc.includes(CREATE_ORDER_VALIDATION_LOC.postalCode)),
  },
  {
    code: CREATE_ORDER_ERROR.invalidAddress,
    match: (detail) => CREATE_ORDER_ADDRESS_FIELDS.some((field) => detail.some((item) => item.loc.includes(field))),
  },
  {
    code: CREATE_ORDER_ERROR.invalidQuantity,
    match: (detail) => detail.some((item) => item.loc.includes(CREATE_ORDER_VALIDATION_LOC.quantity)),
  },
  {
    code: CREATE_ORDER_ERROR.emptyCart,
    match: (detail) => detail.some((item) => item.msg === CREATE_ORDER_API_DETAIL.emptyCart),
  },
  {
    code: CREATE_ORDER_ERROR.requiredField,
    match: (detail) => detail.some((item) => item.msg === CREATE_ORDER_API_DETAIL.fieldRequired),
  },
]

const resolveValidationError = (detail: ValidationError[]): CreateOrderError | null =>
  VALIDATION_ERROR_MATCHERS.find(({ match }) => match(detail))?.code ?? null

const BAD_REQUEST_ERROR_MATCHERS: Array<{ code: CreateOrderError; match: (detail: string) => boolean }> = [
  {
    code: CREATE_ORDER_ERROR.skuNotFound,
    match: (detail) =>
      detail.includes(CREATE_ORDER_API_DETAIL_FRAGMENT.skuId) &&
      detail.includes(CREATE_ORDER_API_DETAIL_FRAGMENT.notFound),
  },
  {
    code: CREATE_ORDER_ERROR.skuZeroQuantity,
    match: (detail) => detail.includes(CREATE_ORDER_API_DETAIL_FRAGMENT.notEnoughStockQuantity),
  },
  {
    code: CREATE_ORDER_ERROR.notEnoughCoins,
    match: (detail) => detail === CREATE_ORDER_API_DETAIL.notEnoughCoins,
  },
  {
    code: CREATE_ORDER_ERROR.wrongAmount,
    match: (detail) => detail === CREATE_ORDER_API_DETAIL.wrongAmount,
  },
  {
    code: CREATE_ORDER_ERROR.noActiveBillingPeriod,
    match: (detail) => detail === CREATE_ORDER_API_DETAIL.noActiveBillingPeriod,
  },
]

const resolveStringDetailError = (detail: string, status?: number): CreateOrderError => {
  if (
    status === HTTP_STATUS.notFound &&
    detail.includes(CREATE_ORDER_API_DETAIL_FRAGMENT.product) &&
    detail.includes(CREATE_ORDER_API_DETAIL_FRAGMENT.notFound)
  ) {
    return CREATE_ORDER_ERROR.productNotFound
  }

  if (status === HTTP_STATUS.badRequest) {
    const matchedError = BAD_REQUEST_ERROR_MATCHERS.find(({ match }) => match(detail))

    if (matchedError) {
      return matchedError.code
    }
  }

  return CREATE_ORDER_ERROR.generic
}

export const resolveCreateOrderError = (error: unknown, status?: number): CreateOrderError => {
  const detail = (error as ApiErrorLike)?.detail

  if (status === HTTP_STATUS.unprocessableEntity && Array.isArray(detail)) {
    return resolveValidationError(detail) ?? CREATE_ORDER_ERROR.generic
  }

  if (typeof detail === 'string') {
    return resolveStringDetailError(detail, status)
  }

  return CREATE_ORDER_ERROR.generic
}
